import type { CatalogueDraftCommand } from "@reminder-tracker/backend-contracts";
import { useMutation } from "convex/react";
import { useRef, useState } from "react";
import { saveCatalogueDraft } from "../../backend/functions";
import { classifyCatalogueFailure } from "./failure";

/** Editorial import, not publication: the server validates the complete content contract. */
export function CatalogueCreateDraft({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: (value: { presetId: string; version: number }) => void;
}): React.JSX.Element {
  const saveDraft = useMutation(saveCatalogueDraft);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [securityFailure, setSecurityFailure] = useState<Error | null>(null);
  const pending = useRef<CatalogueDraftCommand | null>(null);
  const sending = useRef(false);
  if (securityFailure) throw securityFailure;

  const save = async () => {
    if (sending.current) return;
    if (pending.current === null) {
      try {
        const input: unknown = JSON.parse(text);
        if (
          input === null ||
          typeof input !== "object" ||
          Array.isArray(input) ||
          Object.keys(input).sort().join(",") !== "content,expectedRevision,version" ||
          !("version" in input) ||
          input.version !== null ||
          !("expectedRevision" in input) ||
          input.expectedRevision !== null ||
          !("content" in input) ||
          input.content === null ||
          typeof input.content !== "object" ||
          Array.isArray(input.content) ||
          !("presetId" in input.content) ||
          typeof input.content.presetId !== "string"
        )
          throw new Error("Invalid draft envelope");
        // No content is trusted/rendered here. Convex validates every field before writing.
        pending.current = {
          content: input.content as CatalogueDraftCommand["content"],
          version: null,
          expectedRevision: null,
          commandKey: `cmd_${crypto.randomUUID().replaceAll("-", "")}`,
          correlationId: `corr_${crypto.randomUUID().replaceAll("-", "")}`,
        };
      } catch {
        setFeedback(
          "Paste one prepared draft with content, version: null and expectedRevision: null.",
        );
        return;
      }
    }
    sending.current = true;
    setBusy(true);
    setFeedback(null);
    const command = pending.current;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const result = await Promise.race([
        saveDraft(command),
        new Promise<never>((_resolve, reject) => {
          timer = setTimeout(() => reject(new Error("Unconfirmed draft")), 15000);
        }),
      ]);
      pending.current = null;
      setUncertain(false);
      onSaved({ presetId: command.content.presetId, version: result.version });
    } catch (error) {
      const failure = classifyCatalogueFailure(error);
      if (failure.kind === "security")
        setSecurityFailure(
          error instanceof Error
            ? error
            : Object.assign(new Error(failure.message), {
                data: { code: "REAUTHENTICATION_REQUIRED" },
              }),
        );
      if (failure.kind !== "unknown") pending.current = null;
      setUncertain(failure.kind === "unknown");
      setFeedback(failure.message);
    } finally {
      clearTimeout(timer);
      sending.current = false;
      setBusy(false);
    }
  };
  return (
    <div>
      <h3>Create an initial draft</h3>
      <p>
        Import one prepared editorial draft. This saves no review or publication approval and does
        not change customer reminders.
      </p>
      <label style={{ display: "block", marginBlock: "16px" }}>
        Prepared draft JSON
        <textarea
          rows={12}
          maxLength={100000}
          value={text}
          disabled={busy || uncertain}
          style={{ display: "block", width: "100%", boxSizing: "border-box" }}
          onChange={(event) => {
            setText(event.target.value);
            setFeedback(null);
          }}
        />
      </label>
      <p>After saving, inspect the validated draft in the catalogue editor before reviewing it.</p>
      {feedback ? <p role="status">{feedback}</p> : null}
      <button type="button" disabled={busy || text.trim() === ""} onClick={() => void save()}>
        {busy ? "Saving draft…" : uncertain ? "Retry the same draft" : "Save initial draft only"}
      </button>
      <button type="button" disabled={busy || uncertain} onClick={onClose}>
        Cancel initial draft
      </button>
    </div>
  );
}
